/**
 * Demo sync scenarios — simulates what happens when WeHonor re-scans an existing
 * obituary page and detects changes to time, venue, or newly added events.
 *
 * Each scenario targets one of the seeded memorials by id.
 */

import { ImportCandidate } from "../types/domain";

export interface SyncScenario {
  /** The memorial id this scenario targets */
  targetMemorialId: string;
  /** Human-readable label shown in account screen */
  label: string;
  /** What changed — shown as a subtitle */
  changeDescription: string;
  /** The updated candidate that will be diffed against the existing memorial */
  candidate: ImportCandidate;
}

function monarchProductLinks(obituaryUrl: string) {
  return [
    { type: "flowers" as const, label: "Send Flowers", subtitle: "Order flowers for the family", url: `${obituaryUrl}/flowers` },
    { type: "cards" as const, label: "Send a Card", subtitle: "Show your sympathy to the family", url: `${obituaryUrl}/cards` },
    { type: "gifts" as const, label: "Plant Trees", subtitle: "In remembrance", url: `${obituaryUrl}/gifts` },
  ];
}

export const syncScenarios: SyncScenario[] = [
  {
    targetMemorialId: "mem-jennifer-miano",
    label: "Dr. Jennifer Miano — Venue Change",
    changeDescription: "Visitation moved to St. Mark's Parish; reception time extended by 1 hour.",
    candidate: {
      decedentName: "Dr. Jennifer Laura Miano",
      dob: "1968-04-14",
      dod: "2026-03-26",
      obituaryUrl: "https://www.monarchfuneralhome.com/obituary/dr-jennifer-laura-miano",
      photoUrl: "https://www.monarchfuneralhome.com/fh_live/16500/16565/images/obituaries/jennifer-miano.jpg",
      confidence: "high",
      productLinks: monarchProductLinks("https://www.monarchfuneralhome.com/obituary/dr-jennifer-laura-miano"),
      services: [
        {
          title: "Visitation",
          type: "visitation",
          date: "2026-04-05",
          time: "13:00",
          endTime: "16:00",
          // Changed from Monarch Funeral Home to the parish
          venue: "St. Mark's Catholic Church",
          address: "3341 NW 9th Ave, Pompano Beach, FL 33064",
        },
        {
          title: "Funeral Mass",
          type: "funeral",
          date: "2026-04-05",
          time: "16:00",
          endTime: "17:30",
          venue: "St. Mark's Catholic Church",
          address: "3341 NW 9th Ave, Pompano Beach, FL 33064",
          livestreamUrl: "https://www.hdezwebcast.com/show/dr-jennifer-laura-miano",
        },
        {
          title: "Graveside Service",
          type: "graveside",
          date: "2026-04-05",
          time: "18:00",
          endTime: "18:45",
          venue: "Queen of Heaven Cemetery",
          address: "1400 S Lyons Ave, Fort Lauderdale, FL 33316",
        },
      ],
    },
  },
  {
    targetMemorialId: "mem-jimmy-billisi",
    label: "Giacomo Billisi — Livestream Added",
    changeDescription: "Family added a webcast link for the funeral mass; reception extended 30 minutes.",
    candidate: {
      decedentName: "Giacomo \"Jimmy\" Billisi",
      dob: "1952-07-18",
      dod: "2026-03-25",
      obituaryUrl: "https://www.monarchfuneralhome.com/obituary/giacomo-jimmy-billisi",
      confidence: "high",
      productLinks: monarchProductLinks("https://www.monarchfuneralhome.com/obituary/giacomo-jimmy-billisi"),
      services: [
        {
          title: "Funeral Mass",
          type: "funeral",
          date: "2026-04-02",
          time: "11:00",
          endTime: "12:00",
          venue: "Holy Rosary Catholic Church",
          address: "7500 Stirling Rd, Hollywood, FL 33024",
          // Newly added livestream
          livestreamUrl: "https://www.hdezwebcast.com/show/giacomo-billisi",
        },
        {
          title: "Reception",
          type: "reception",
          date: "2026-04-02",
          time: "13:00",
          // Extended by 30 minutes
          endTime: "15:30",
          venue: "Monarch Funeral Home & Cremation Services",
          address: "2950 N State Rd 7, Margate, FL 33063",
        },
      ],
    },
  },
  {
    targetMemorialId: "mem-shane-douglas",
    label: "Shane Douglas — New Event Added",
    changeDescription: "A Celebration of Life reception was added as a second event.",
    candidate: {
      decedentName: "Shane Douglas",
      dob: "1979-11-30",
      dod: "2026-03-24",
      obituaryUrl: "https://www.monarchfuneralhome.com/obituary/shane-douglas",
      confidence: "high",
      productLinks: monarchProductLinks("https://www.monarchfuneralhome.com/obituary/shane-douglas"),
      services: [
        {
          title: "Memorial Service",
          type: "memorial_service",
          date: "2026-04-04",
          time: "10:00",
          endTime: "11:30",
          venue: "Monarch Funeral Home & Cremation Services",
          address: "2950 N State Rd 7, Margate, FL 33063",
        },
        {
          // New — not in the original seed
          title: "Celebration of Life",
          type: "celebration_of_life",
          date: "2026-04-04",
          time: "13:00",
          endTime: "16:00",
          venue: "Pompano Beach Civic Center",
          address: "1801 NE 6th St, Pompano Beach, FL 33060",
        },
      ],
    },
  },
];

/** Return the next unplayed scenario index from a given played-count */
export function getNextSyncScenario(playedCount: number): SyncScenario | null {
  return syncScenarios[playedCount % syncScenarios.length] ?? null;
}
